import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Barbell, Envelope, LockKey, User as UserIcon, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const TABS = [
  { key: 'signin', label: 'Sign In' },
  { key: 'signup', label: 'Create Account' },
];

export default function SignIn() {
  const navigate = useNavigate();
  const { signInEmail, signUpEmail, continueAsGuest } = useAuth();

  const [tab, setTab] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (tab === 'signup' && password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      if (tab === 'signup') {
        await signUpEmail({ email, password, display_name: name || email.split('@')[0] });
      } else {
        await signInEmail({ email, password });
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const guest = async () => {
    setError('');
    setGuestLoading(true);
    try {
      await continueAsGuest();
      // Route to onboarding tutorial on first guest sign-in
      localStorage.setItem('breth_show_tutorial', '1');
      navigate('/', { replace: true });
    } catch (err) {
      setError('Could not start guest session');
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="mobile-container min-h-screen px-6 pt-16 pb-10 flex flex-col">
      {/* Brand */}
      <div className="mb-10">
        <div className="w-14 h-14 rounded-2xl bg-graphite text-bone flex items-center justify-center mb-4 shadow-ios">
          <Barbell size={28} weight="fill" />
        </div>
        <h1 className="text-5xl font-black font-heading uppercase text-graphite leading-none tracking-tight">
          BRETH
        </h1>
        <p className="text-sm text-graphite/60 mt-2 uppercase tracking-[0.22em] font-bold">
          Move heavy. Log light.
        </p>
      </div>

      {/* Tab switcher */}
      <div className="ios-card p-1 flex gap-1 mb-6" data-testid="auth-tab-switcher">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setError(''); }}
            className={`flex-1 h-11 rounded-ios-pill text-[11px] uppercase tracking-[0.18em] font-black transition-colors ${
              tab === t.key ? 'bg-graphite text-bone' : 'text-graphite/60'
            }`}
            data-testid={`auth-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Form */}
      <form onSubmit={submit} className="space-y-3" data-testid={`auth-form-${tab}`}>
        {tab === 'signup' && (
          <Field
            icon={<UserIcon size={16} weight="bold" />}
            placeholder="Display Name (optional)"
            value={name}
            onChange={setName}
            testId="auth-name-input"
            inputMode="text"
            autoCapitalize="words"
          />
        )}
        <Field
          icon={<Envelope size={16} weight="bold" />}
          type="email"
          placeholder="Email"
          value={email}
          onChange={setEmail}
          testId="auth-email-input"
          inputMode="email"
          autoCapitalize="none"
          required
        />
        <Field
          icon={<LockKey size={16} weight="bold" />}
          type="password"
          placeholder={tab === 'signup' ? 'Password (8+ characters)' : 'Password'}
          value={password}
          onChange={setPassword}
          testId="auth-password-input"
          required
        />
        {error && (
          <p
            className="text-[11px] uppercase tracking-[0.14em] font-bold text-userRed"
            data-testid="auth-error"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-14 rounded-ios-pill bg-userRed text-bone font-black text-xs uppercase tracking-[0.18em] flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid={`auth-submit-${tab}`}
          data-haptic="success"
        >
          {loading ? 'Please wait…' : (tab === 'signup' ? 'Create Account' : 'Sign In')}
          <ArrowRight size={16} weight="bold" />
        </button>
      </form>

      <div className="mt-auto pt-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="flex-1 h-px bg-hairline" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/40">Or</span>
          <span className="flex-1 h-px bg-hairline" />
        </div>
        <button
          onClick={guest}
          disabled={guestLoading}
          className="w-full h-12 rounded-ios-pill border border-hairline text-[11px] uppercase tracking-[0.18em] font-bold text-graphite/70 hover:bg-graphite/5"
          data-testid="auth-guest-button"
        >
          {guestLoading ? 'Starting…' : 'Continue as Guest'}
        </button>
        <p className="text-[10px] text-graphite/40 uppercase tracking-[0.15em] font-bold text-center mt-3">
          Guest data lives on this device only
        </p>
      </div>
    </div>
  );
}

function Field({ icon, testId, onChange, value, ...rest }) {
  return (
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-graphite/40">{icon}</span>
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-14 pl-11 pr-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
        data-testid={testId}
      />
    </div>
  );
}
